/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  downloadFile,
  Boton,
  Titulo,
  CampoInformacion,
  Formulario,
  useFuncionalidad,
  tituloBase,
  TablaFiltrable
} from "../../../../shared";
import TarjetaAsignarEsquema from "./TarjetaAsignarEsquema";
import TarjetaAsignarNorma from "./TarjetaAsignarNorma";
import TarjetaAsignarTemplate from "./TarjetaAsignarTemplate";
import ProductoCamposPropios from "./ProductoCamposPropios";
import ProductoCaracteristicasCriticas from "./ProductoCaracteristicasCriticas";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Sidebar } from "../../../../shared";
import { Divider } from "primereact/divider";
import { ConfirmDialog } from "primereact/confirmdialog";

export default function CertificadoTipo() {
  const conUrsea = useFuncionalidad("URSEA");
  const [certificadoTipo, setCertificadoTipo] = useState(null);
  // Cuál de los dos formularios está abierto: "detalles" o "asistente".
  // Ambos guardan contra el mismo endpoint; lo que cambia es qué campos manda
  // cada uno.
  const [sidebarAbierto, setSidebarAbierto] = useState(null);
  const [loaderEnvio, setLoaderEnvio] = useState(false);
  const [loaderDescargarPlantilla, setLoaderDescargarPlantilla] =
    useState(false);
  const { certificadoTipoId } = useParams();
  const toast = useRef(null);

  useEffect(() => {
    cargarCertificadoTipo();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cargarCertificadoTipo() {
    accessAPI(
      "GET",
      `admin/producto/certificadotipo/${certificadoTipoId}`,
      null,
      (respuesta) => {
        setCertificadoTipo(respuesta);
        document.title = respuesta.nombre;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar el tipo de certificado",
          sticky: true
        });
      }
    );
  }

  const asistenteFormConfig = certificadoTipo
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "instruccionesAlAsistente",
                tipoitemformularioid: 11,
                etiqueta: "Instrucciones para el asistente de IA",
                respuesta: certificadoTipo.instruccionesAlAsistente
              },
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar",
            callback: editarCertificadoTipoSubmit,
            validar: true
          }
        ]
      }
    : null;

  function editarCertificadoTipoSubmit(respuestasFormulario) {
    setLoaderEnvio(true);
    accessAPI(
      "PUT",
      `admin/config/certificadotipo/${certificadoTipoId}`,
      respuestasFormulario,
      (respuesta) => {
        setCertificadoTipo((prev) => ({ ...prev, ...respuesta }));
        document.title = respuesta.nombre;
        setSidebarAbierto(null);
        setLoaderEnvio(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Tipo de certificado actualizado exitosamente"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg || "Error al actualizar el tipo de certificado",
          sticky: true
        });
        setLoaderEnvio(false);
      }
    );
  }

  function descargarPlantilla() {
    setLoaderDescargarPlantilla(true);
    downloadFile(
      `admin/proceso/plantillaSolicitud/${certificadoTipo.id}`,
      `Plantilla productos - ${certificadoTipo.nombre}`,
      () => setLoaderDescargarPlantilla(false),
      (respuesta) => {
        setLoaderDescargarPlantilla(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar la plantilla",
          sticky: true
        });
      }
    );
  }

  const editarFormConfig = certificadoTipo
    ? {
        secciones: [
          {
            campos: [
              // El código en URSEA solo se edita si el organismo tiene la
              // integración prendida.
              ...(conUrsea
                ? [
                    {
                      nombre: "codigo",
                      tipoitemformularioid: 1,
                      etiqueta: "Código URSEA",
                      respuesta: certificadoTipo.codigo
                    }
                  ]
                : []),
              {
                nombre: "nombre",
                tipoitemformularioid: 1,
                etiqueta: "Nombre",
                requerido: true,
                respuesta: certificadoTipo.nombre
              },
              {
                nombre: "reglamentacion",
                tipoitemformularioid: 1,
                etiqueta: "Reglamentación",
                requerido: true,
                respuesta: certificadoTipo.reglamentacion
              },
              {
                nombre: "procesotipoid",
                tipoitemformularioid: 5,
                etiqueta: "Tipo de proceso",
                opciones: "admin/config/procesotipo",
                sinOpcionInicial: true,
                requerido: true,
                respuesta: certificadoTipo.procesotipoid
              },
              {
                nombre: "duracioncertificado",
                tipoitemformularioid: 4,
                etiqueta: "Duración del certificado (años)",
                requerido: true,
                respuesta: certificadoTipo.duracioncertificado
              },
              {
                nombre: "textolegalcertificado",
                tipoitemformularioid: 11,
                etiqueta: "Texto legal en el certificado",
                requerido: true,
                respuesta: certificadoTipo.textolegalcertificado
              },
              {
                nombre: "notasPropuesta",
                tipoitemformularioid: 11,
                etiqueta: "Notas para la propuesta comercial",
                respuesta: certificadoTipo.notasPropuesta
              },
              {
                nombre: "disponiblesolicitud",
                tipoitemformularioid: 6,
                etiqueta: "Disponible para solicitud",
                respuesta: certificadoTipo.disponiblesolicitud
              },
              {
                nombre: "disponibleproceso",
                tipoitemformularioid: 6,
                etiqueta: "Disponible para procesos",
                respuesta: certificadoTipo.disponibleproceso
              },
              ...(conUrsea
                ? [
                    {
                      nombre: "gestionadoursea",
                      tipoitemformularioid: 6,
                      etiqueta: "Gestionado por URSEA",
                      respuesta: certificadoTipo.gestionadoursea
                    }
                  ]
                : []),
              {
                nombre: "sinrenovacion",
                tipoitemformularioid: 6,
                etiqueta: "El certificado no tiene renovación",
                respuesta: certificadoTipo.sinrenovacion
              },
              ...(conUrsea
                ? [
                    {
                      nombre: "nombreursea",
                      tipoitemformularioid: 1,
                      etiqueta: "Nombre en el certificado digital de URSEA",
                      respuesta: certificadoTipo.nombreursea
                    }
                  ]
                : []),
              {
                nombre: "ncm",
                tipoitemformularioid: 13,
                etiqueta: "NCM",
                notas:
                  "Ingresar con puntos y los 10 dígitos completos (ej. 8504.40.10.20): 6 del Sistema Armonizado + 2 del NCM Mercosur + 2 del ítem nacional uruguayo. Puede quedar vacío. Se usa para el cruce de importaciones con Penta-Transaction.",
                respuesta: certificadoTipo.ncm || []
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarCertificadoTipoSubmit,
            validar: true
          }
        ]
      }
    : null;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />

      {!certificadoTipo && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          {/* Título */}
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="15rem" height="2rem" />
            </div>
            <Skeleton width="10rem" height="2.5rem" />
          </div>

          {/* Card detalles */}
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div className="grid">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="col-12 md:col-4">
                  <Skeleton
                    width="50%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              ))}
            </div>
            <Divider />
            <div className="grid">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="col-12 md:col-4">
                  <Skeleton
                    width="60%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="70%" height="1.2rem" />
                </div>
              ))}
            </div>
            <Divider />
            <Skeleton
              width="40%"
              height="0.8rem"
              css={{ marginBottom: "0.5rem" }}
            />
            <Skeleton width="100%" height="2.5rem" />
            <Divider />
            <div className="grid">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="col-12 md:col-4">
                  <Skeleton
                    width="55%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="3rem" height="1.2rem" />
                </div>
              ))}
            </div>
          </Card>

          {/* Grid dos columnas */}
          <div className="grid">
            <div
              className="col-12 lg:col-6"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              {[...Array(2)].map((_, i) => (
                <Card key={i}>
                  <Skeleton
                    width="8rem"
                    height="1.3rem"
                    css={{ marginBottom: "1em" }}
                  />
                  <Skeleton width="100%" height="2.5rem" />
                </Card>
              ))}
            </div>
            <div
              className="col-12 lg:col-6"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <Skeleton
                    width="10rem"
                    height="1.3rem"
                    css={{ marginBottom: "1em" }}
                  />
                  <Skeleton width="100%" height="2.5rem" />
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {certificadoTipo && (
        <>
          <Titulo
            urlVolver="/certificadotipos"
            botones={
              <Boton
                transparente
                callback={descargarPlantilla}
                loader={loaderDescargarPlantilla}
              >
                <i className="pi pi-download" /> Plantilla de solicitud
              </Boton>
            }
          >
            {certificadoTipo.nombre}
          </Titulo>

          <Card>
            <Titulo
              botones={
                <Boton icono negro callback={() => setSidebarAbierto("detalles")}>
                  <i className="pi pi-pencil" />
                </Boton>
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              {conUrsea && (
                <CampoInformacion nombreCampo="Código URSEA">
                  {certificadoTipo.codigo || "-"}
                </CampoInformacion>
              )}
              <CampoInformacion nombreCampo="Nombre">
                {certificadoTipo.nombre}
              </CampoInformacion>
              {conUrsea && certificadoTipo.gestionadoursea && (
                <CampoInformacion nombreCampo="Nombre certificado URSEA">
                  {certificadoTipo.nombreursea || "-"}
                </CampoInformacion>
              )}
            </div>
            <Divider />
            <div className="grid">
              <CampoInformacion nombreCampo="Tipo de proceso">
                {certificadoTipo.procesotipo?.nombre}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Reglamentación">
                {certificadoTipo.reglamentacion}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Duración del certificado (años)">
                {certificadoTipo.duracioncertificado}
              </CampoInformacion>
            </div>
            <Divider />
            <div className="grid">
              <CampoInformacion nombreCampo="Texto legal en el certificado" anchoCompleto>
                {certificadoTipo.textolegalcertificado}
              </CampoInformacion>
            </div>
            <Divider />
            <div className="grid">
              <CampoInformacion nombreCampo="Notas para la propuesta comercial" anchoCompleto>
                <span css={{ whiteSpace: "pre-wrap" }}>
                  {certificadoTipo.notasPropuesta || "-"}
                </span>
              </CampoInformacion>
            </div>
            <Divider />
            <div className="grid">
              <CampoInformacion nombreCampo="Disponible para solicitud">
                {certificadoTipo.disponiblesolicitud ? "Sí" : "No"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Disponible para procesos">
                {certificadoTipo.disponibleproceso ? "Sí" : "No"}
              </CampoInformacion>
              {conUrsea && (
                <CampoInformacion nombreCampo="Gestionado por URSEA">
                  {certificadoTipo.gestionadoursea ? "Sí" : "No"}
                </CampoInformacion>
              )}
              <CampoInformacion nombreCampo="Sin renovación">
                {certificadoTipo.sinrenovacion ? "Sí" : "No"}
              </CampoInformacion>
            </div>
            <Divider />
            <div className="grid">
              <CampoInformacion nombreCampo="NCM" anchoCompleto>
                {certificadoTipo.ncm?.length > 0
                  ? certificadoTipo.ncm.join(", ")
                  : "-"}
              </CampoInformacion>
            </div>
          </Card>

          <Card css={{ marginTop: "1em" }}>
            <Titulo
              botones={
                <Boton icono negro callback={() => setSidebarAbierto("asistente")}>
                  <i className="pi pi-pencil" />
                </Boton>
              }
            >
              Instrucciones para el asistente de IA
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Instrucciones" anchoCompleto>
                <span css={{ whiteSpace: "pre-wrap" }}>
                  {certificadoTipo.instruccionesAlAsistente || "-"}
                </span>
              </CampoInformacion>
            </div>
          </Card>

          <div className="grid" css={{ marginTop: "1em" }}>
            <div
              className="col-12 lg:col-6"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              <TarjetaAsignarNorma
                normas={certificadoTipo.certificadotipo_x_norma}
                certificadoTipoId={certificadoTipoId}
              />
              <TarjetaAsignarEsquema
                esquemas={certificadoTipo.certificadotipo_x_esquema}
                certificadoTipoId={certificadoTipoId}
              />
              <Card>
                {/* Solo lectura: la vinculación se maneja desde cada acción,
                    en Configuración → Acciones para clientes */}
                <Titulo>Acciones para el cliente</Titulo>
                <TablaFiltrable conFiltros={false}
                  value={certificadoTipo.certificadotipo_x_accioncliente || []}
                  nombreArchivo="acciones_tipo_certificado"
                  emptyMessage="Este tipo no tiene acciones vinculadas"
                  size="small"
                >
                  <Column
                    header="Nombre"
                    body={(vinculo) => vinculo.accioncliente?.nombre}
                  />
                </TablaFiltrable>
              </Card>
            </div>
            <div
              className="col-12 lg:col-6"
              css={{ display: "flex", flexDirection: "column", gap: "1em" }}
            >
              <TarjetaAsignarTemplate
                certificadoTipoId={certificadoTipoId}
                TemplateDocumento={certificadoTipo.TemplateDocumentoCertificado}
                TemplateEtiqueta={certificadoTipo.TemplateEtiqueta}
                TemplateEtiquetaDetalles={certificadoTipo.templateEtiquetaDetalles}
              />

              <ProductoCamposPropios
                productoCamposPropios={certificadoTipo.productocampospropios}
                certificadoTipoId={certificadoTipoId}
              />

              <ProductoCaracteristicasCriticas
                productoCaracteristicasCriticas={
                  certificadoTipo.ProductoCaracteristicasCriticas
                }
                certificadoTipoId={certificadoTipoId}
              />
            </div>
          </div>

          <Sidebar
            visible={sidebarAbierto === "detalles"}
            position="right"
            onHide={() => setSidebarAbierto(null)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar tipo de certificado</Titulo>
            {editarFormConfig && (
              <Formulario
                key={certificadoTipo.id + "-" + certificadoTipo.nombre}
                config={editarFormConfig}
                loader={loaderEnvio}
              />
            )}
          </Sidebar>

          <Sidebar
            visible={sidebarAbierto === "asistente"}
            position="right"
            onHide={() => setSidebarAbierto(null)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Instrucciones para el asistente de IA</Titulo>
            {asistenteFormConfig && (
              <Formulario
                key={`asistente-${certificadoTipo.id}`}
                config={asistenteFormConfig}
                loader={loaderEnvio}
              />
            )}
          </Sidebar>
        </>
      )}
    </>
  );
}
